import { RegionSetStatusType } from "@app/projects/enumerators";
import { AggregateDisplayType, SpecialtyGroupEntityType } from "@app/projects/literals";
import { AccuracyAggregateInspectionResultGroup } from "@app/projects/results/accuracy/accuracyAggregateInspectionResultGroup";
import { AccuracyBandAggregate } from "@app/projects/results/accuracy/accuracyBandAggregate";
import { AccuracyRegionSetAggregate } from "@app/projects/results/accuracy/accuracyRegionSetAggregate";
import { AccuracySpecialtyGroupAggregate } from "@app/projects/results/accuracy/accuracySpecialtyGroupAggregate";
import {
  AnalysisSpecialtyGroup,
  ResultRegion,
} from "@app/projects/results/common";
import { RegionGranularity } from "@app/region-sets";

interface AccuracyAggregate {
  key: string;
  defaultDisplayOrder: number;
  displayText: string;
  linkSuffix: string;

  providerCount: number;
  accuracyIndex: number;
  providerPassPercentage: number;
  providerFailPercentage: number;
  resultGroups: AccuracyAggregateInspectionResultGroup[];
  bands: AccuracyBandAggregate[];

  regionCode: string;
  regionSetId: number;
  regionSetDisplayName: string;
  regionStatus?: RegionSetStatusType;
  regionGranularity: RegionGranularity;
  subRegionCode: string;

  specialtyGroupCode?: string;
  specialtyGroupDisplayName?: string;
  specialtyGroupDisplayOrder?: number;
  specialtyGroupEntityType?: SpecialtyGroupEntityType;
  specialtyGroupIsInformational?: boolean;
  specialtyGroupName?: string;

  recentlyAttestedCount?: number;
  recentlyAttestedPercent?: number;

  ninetyDayAttestedCount?: number;
  ninetyDayAttestedPercent?: number;

  parentAbbreviation?: string;
  parentDisplayName?: string;
}

class AccuracyAggregateImpl implements AccuracyAggregate {
  public constructor(
    public key: string,
    private aggregateDisplayType: AggregateDisplayType,

    public providerCount: number,
    public accuracyIndex: number,
    public providerPassPercentage: number,
    public providerFailPercentage: number,
    public resultGroups: AccuracyAggregateInspectionResultGroup[],
    public bands: AccuracyBandAggregate[],

    public regionCode: string,
    public regionGranularity: RegionGranularity,
    public regionSetDisplayName: string,
    public regionSetId: number,
    public subRegionCode: string,

    public regionStatus?: RegionSetStatusType,

    public specialtyGroupCode?: string,
    public specialtyGroupDisplayName?: string,
    public specialtyGroupDisplayOrder?: number,
    public specialtyGroupEntityType?: SpecialtyGroupEntityType,
    public specialtyGroupIsInformational?: boolean,
    public specialtyGroupName?: string,

    public recentlyAttestedCount?: number,
    public recentlyAttestedPercent?: number,

    public ninetyDayAttestedCount?: number,
    public ninetyDayAttestedPercent?: number,

    public parentAbbreviation?: string,
    public parentDisplayName?: string,
  ) { }

  public get defaultDisplayOrder(): number {
    switch (this.aggregateDisplayType) {
      case "region":
        return this.regionSetId;
      case "specialty":
        return this.specialtyGroupDisplayOrder;
      case "unknown":
        return null;
    }
  }

  public get displayText(): string {
    switch (this.aggregateDisplayType) {
      case "region":
        return this.regionSetDisplayName;
      case "specialty":
        return this.specialtyGroupDisplayName;
      case "unknown":
        return null;
    }
  }

  public get linkSuffix(): string {
    switch (this.aggregateDisplayType) {
      case "region":
        return `/${this.regionGranularity}/${this.regionCode}`;
      case "specialty":
        return `/specialty/${this.specialtyGroupCode}`;
      case "unknown":
        return null;
    }
  }
}

function buildAccuracyAggregate(
  key: string,
  resultRegion: ResultRegion,
  aggregate: AccuracyRegionSetAggregate | AccuracySpecialtyGroupAggregate,
  aggregateDisplayType: AggregateDisplayType,
  specialtyGroup?: AnalysisSpecialtyGroup,
  regionStatus?: RegionSetStatusType,
): AccuracyAggregate {
  let newAggregate: AccuracyAggregate = {} as AccuracyAggregate;

  if (!resultRegion || !aggregate) {
    return newAggregate;
  }

  if (isSpecialtyGroupAggregate(aggregate)) {
    newAggregate = new AccuracyAggregateImpl(
      key,
      aggregateDisplayType,

      aggregate.providerCount,
      aggregate.accuracyIndex,
      aggregate.providerPassPercentage,
      aggregate.providerFailPercentage,
      aggregate.resultGroups,
      aggregate.bands,

      resultRegion.regionCode,
      resultRegion.regionGranularity,
      resultRegion.displayName,
      resultRegion.regionSetId,
      resultRegion.subRegionCode,

      regionStatus,

      specialtyGroup.code,
      `${specialtyGroup.code} - ${specialtyGroup.name}`,
      specialtyGroup.displayOrder,
      specialtyGroup.entityType,
      specialtyGroup.isInformational,
      specialtyGroup.name,

      aggregate.recentlyAttestedCount,
      aggregate.recentlyAttestedPercent,

      aggregate.ninetyDayAttestedCount,
      aggregate.ninetyDayAttestedPercent
    );
  } else {
    newAggregate = new AccuracyAggregateImpl(
      key,
      aggregateDisplayType,

      aggregate.providerCount,
      aggregate.accuracyIndex,
      aggregate.providerPassPercentage,
      aggregate.providerFailPercentage,
      aggregate.resultGroups,
      aggregate.bands,

      resultRegion.regionCode,
      resultRegion.regionGranularity,
      resultRegion.displayName,
      resultRegion.regionSetId,
      resultRegion.subRegionCode,

      regionStatus,

      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,

      aggregate.recentlyAttestedCount,
      aggregate.recentlyAttestedPercent,

      aggregate.ninetyDayAttestedCount,
      aggregate.ninetyDayAttestedPercent
    );
  }

  return newAggregate;
}

/**
 * Clones an `AccuracyAggregate` while setting a new displayText property based on the `AggregateDisplayType`
 * It DOES actually create a new object
 */
function pseudoClone(
  aggregate: AccuracyAggregate,
  aggregateDisplayType: AggregateDisplayType,
): AccuracyAggregate {
  if (!aggregate) {
    return {} as AccuracyAggregate;
  }
  return new AccuracyAggregateImpl(
    aggregate.key,
    aggregateDisplayType,

    aggregate.providerCount,
    aggregate.accuracyIndex,
    aggregate.providerPassPercentage,
    aggregate.providerFailPercentage,
    aggregate.resultGroups,
    aggregate.bands,

    aggregate.regionCode,
    aggregate.regionGranularity,
    aggregate.regionSetDisplayName,
    aggregate.regionSetId,
    aggregate.subRegionCode,

    aggregate.regionStatus,

    aggregate.specialtyGroupCode,
    aggregate.specialtyGroupDisplayName,
    aggregate.specialtyGroupDisplayOrder,
    aggregate.specialtyGroupEntityType,
    aggregate.specialtyGroupIsInformational,
    aggregate.specialtyGroupName,

    aggregate.recentlyAttestedCount,
    aggregate.recentlyAttestedPercent,

    aggregate.ninetyDayAttestedCount,
    aggregate.ninetyDayAttestedPercent,

    aggregate.parentAbbreviation,
    aggregate.parentDisplayName,
  );
}

function isSpecialtyGroupAggregate(aggregate: AccuracyRegionSetAggregate | AccuracySpecialtyGroupAggregate): aggregate is AccuracySpecialtyGroupAggregate {
  const agg = aggregate as AccuracySpecialtyGroupAggregate;
  return agg.specialtyGroupCode !== undefined && agg.specialtyGroupCode !== null;
}

export { AccuracyAggregate, buildAccuracyAggregate, pseudoClone };
